import { convertToBoolean, isValidUUID, paramify, Use } from '@appbolaget/helpers';
import { __decorate } from 'tslib';

function Boolean() {
    return (_, __) => {
        const key = Symbol();
        return {
            get() {
                return this[key];
            },
            set(next) {
                this[key] = convertToBoolean(next);
            },
        };
    };
}

function Expand(model, isArray = false) {
    return (target, propertyKey) => {
        const values = new WeakMap();
        Object.defineProperty(target, propertyKey, {
            set(newValue) {
                // This `setter` gets called once per new instance, and only the
                // first time we set the value of the target property.
                // Here we have access to the instance `this`, so we define
                // a property with the same name on the class instance.
                Object.defineProperty(this, propertyKey, {
                    get() {
                        // This `getter` gets called every time we read the instance property.
                        // We simply look up the instance in our map and return its value.
                        return values.get(this);
                    },
                    set(next) {
                        // This `setter` is called every time we set the value of the
                        // property on the class instance.
                        values.set(this, next ? (!isArray ? new model(next) : next?.map((obj) => new model(obj))) : null);
                    },
                    configurable: true,
                });
                // Finally we set the value of property on the class instance.
                // This will trigger the `setter` on the instance that we defined above.
                return (this[propertyKey] = newValue);
            },
            configurable: true,
        });
    };
}

function Float() {
    return (_, __) => {
        const key = Symbol();
        return {
            get() {
                return this[key];
            },
            set(next) {
                this[key] = next ? parseFloat(next) : null;
            },
        };
    };
}

function Integer() {
    return (_, __) => {
        const key = Symbol();
        return {
            get() {
                return this[key];
            },
            set(next) {
                this[key] = next ? parseInt(next, 10) : next === 0 ? 0 : null;
            },
        };
    };
}

function Json() {
    return (_, __) => {
        const key = Symbol();
        return {
            get() {
                return this[key];
            },
            set(next) {
                this[key] = typeof next === 'string' ? JSON.parse(next) : next;
            },
        };
    };
}

class AddressMixin {
    getAddressWithType(type) {
        if (!this.addresses || !this.addresses.length) {
            return null;
        }
        const communication = this.addresses.find((obj) => obj.type === type);
        if (!communication) {
            return null;
        }
        return communication;
    }
}

class AttributeMixin {
    /**
     * Helper method to get an attribute value by key
     * @param key Attribute key
     * @param property Property of attribute: simple, text or json
     * @param returns Attribute property value: string
     */
    attr(key, property = 'simple') {
        const found = this.attributes?.find((attr) => attr.key === key);
        return found ? found[property] : null;
    }
    /**
     * Helper method to return many attributes of the same key
     * @param key Attribute key
     * @param property Property of attribute: simple, text or json
     * @returns Array of attribute values
     */
    attrs(key, property = 'simple') {
        return this.attributes?.filter((attr) => attr.key === key).map((attr) => attr[property]);
    }
    /**
     * Helper method to get an entire attribute object by key
     * @param key Attribute key
     * @param returns Attribute model
     */
    attribute(key) {
        return this.attributes?.find((attr) => attr.key === key);
    }
    /**
     * Helper method to get an entire attribute object by key
     * @param key Attribute key
     * @returns Attribute array
     */
    attributeList(key) {
        return this.attributes?.filter((attr) => attr.key === key);
    }
}

class CommunicationMixin {
    getCommunicationWithType(type) {
        if (!this.communications || !this.communications.length) {
            return null;
        }
        const communication = this.communications.find((obj) => obj.type === type);
        if (!communication) {
            return null;
        }
        return communication;
    }
}

// @dynamic
class AwareModel {
    constructor(data, model) {
        this.model = model;
        if (data) {
            this._setData(data);
        }
    }
    _setData(data) {
        if (this.model.fields) {
            for (const field of [...this.model.fields, ...AwareModel.defaultFields]) {
                this[field] = data[field];
            }
        }
        else {
            Object.keys(data).forEach((key) => {
                this[key] = data[key];
            });
        }
    }
    /**
     * Prepare a data model for an http request, strip out unneccessary fields and such.
     * @param fields Array of string representing the fields we want to include in our request
     * @param data The data model we have to work with
     */
    static prepare(fields, data) {
        return fields
            .filter((field) => !AwareModel.excludeFromHttp.includes(field))
            .reduce((acc, curr) => {
            // We can expect this to be a Meta array
            if (curr === 'meta' && Array.isArray(data[curr])) {
                acc[curr] = JSON.stringify(data[curr].map((meta) => AwareModel.prepare(meta.model.fields.filter((field) => !meta.model.excludeFromHttp.includes(field)), meta)));
            }
            else {
                acc[curr] = data[curr];
            }
            return acc;
        }, {});
    }
}
AwareModel.defaultFields = ['localProps'];
AwareModel.excludeFromHttp = [
    'children',
    'created_at',
    'id',
    'localProps',
    'media',
    'model',
    'parent',
    'pivot-children',
    'pivot-parents',
    'unit',
    'updated_at',
    'uuid',
];
__decorate([
    Integer()
], AwareModel.prototype, "id", void 0);

class Meta extends AwareModel {
    constructor(data, model = Meta) {
        super(data, model);
    }
}
Meta.fields = ['id', 'resource_id', 'unique', 'delete', 'key', 'value', 'multiple'];
Meta.excludeFromHttp = ['delete'];
__decorate([
    Integer()
], Meta.prototype, "resource_id", void 0);
__decorate([
    Boolean()
], Meta.prototype, "unique", void 0);
__decorate([
    Boolean()
], Meta.prototype, "delete", void 0);
__decorate([
    Boolean()
], Meta.prototype, "multiple", void 0);

class MetaMixin {
    constructor() {
        this.hasMeta = false;
    }
    setMeta(meta) {
        this.hasMeta = true;
        this.meta = [];
        if (meta.constructor === Object) {
            for (const key in meta) {
                if (meta[key].value !== null) {
                    this.meta.push(new Meta(meta[key]));
                }
            }
        }
        else if (meta.constructor === Array) {
            for (const obj of meta) {
                this.meta.push(new Meta(obj));
            }
        }
    }
    createMeta(key, value) {
        const meta = new Meta({ key, value });
        this.meta.push(meta);
    }
    deleteMetaByKey(key) {
        this.meta = this.meta.filter((meta) => meta.key !== key);
    }
    updateMeta(meta) {
        for (const key in meta) {
            if (meta[key]) {
                for (const obj of this.meta) {
                    if (obj.key === key) {
                        obj.value = meta[key];
                        continue;
                    }
                }
            }
        }
    }
    getMetaField(key, substitute) {
        if (!this.meta) {
            return substitute || null;
        }
        for (const obj of this.meta) {
            if (key === obj.key) {
                return obj;
            }
        }
        return substitute || null;
    }
    getMetaFields(key, substitute) {
        if (!this.meta) {
            return substitute || null;
        }
        const ret = [];
        for (const obj of this.meta) {
            if (key === obj.key) {
                ret.push(obj);
            }
        }
        return ret;
    }
    getMeta(key, substitute) {
        if (!this.meta) {
            return substitute || '';
        }
        for (const obj of this.meta) {
            if (key === obj.key) {
                if (obj.value === '') {
                    return substitute || '';
                }
                return obj.value;
            }
        }
        return substitute || '';
    }
    getMetaCollection(key, substitute) {
        if (!this.meta) {
            return substitute || [];
        }
        const ret = [];
        for (const obj of this.meta) {
            if (key === obj.key) {
                ret.push(obj.value);
            }
        }
        return ret;
    }
    getMetaFieldValue(key, substitute) {
        return this.getMeta(key, substitute);
    }
    getMetaFieldsValue(key, substitute) {
        return this.getMetaCollection(key, substitute);
    }
}
__decorate([
    Expand(Meta, true)
], MetaMixin.prototype, "meta", void 0);

class Attribute extends AwareModel {
    constructor(data, model = Attribute) {
        super(data, model);
    }
}
Attribute.fields = ['created_at', 'id', 'uuid', 'alias', 'resource_id', 'key', 'simple', 'text', 'json', 'datetime', 'updated_at'];
__decorate([
    Integer()
], Attribute.prototype, "resource_id", void 0);

class CrudMixin {
    /**
     * Refetches the current model instance
     * @param api AwareHttpService
     * @param route Optional route
     */
    get(api, route) {
        return api.get(route || `${this.model.route}/${this.uuid}`).toModel(this.model);
    }
    /**
     * Sends a delete request to the api on the current model instance
     * @param api AwareHttpService
     * @param options Optional properties includes a different route and
     * data to pass along the request
     */
    delete(api, options) {
        return api.delete(options?.route || this.model.route, options?.data, this.uuid);
    }
    /**
     *
     * @param api AwareHttpService
     * @param options Optional properties includes a fields object to limit the amount of data
     * sent with the request. For instance, if you know you have only changed the title of an
     * object, send in 'title' in the fields property to only send that. You can also pass an
     * optional route if the model base route doesn't cut it.
     */
    save(api, options) {
        const fields = options?.fields;
        const route = options?.route ? options.route : this.model.route;
        const finalFields = fields
            ? Array.isArray(fields)
                ? fields
                : [fields]
            : (this.model.fields ? this.model.fields : Object.keys(this)).filter((field) => !this.model.excludeFromHttp.includes(field));
        const data = options?.data || AwareModel.prepare(finalFields, this);
        let request = api.post(route, data);
        if (isValidUUID(this.uuid)) {
            request = api.put(route, data, this.uuid);
        }
        return request;
    }
}

class File extends AwareModel {
    constructor(data, model = File) {
        super(data, model);
        this.details = [];
        if (!data.title || !data.title.length) {
            this.title = data.filename;
        }
    }
    getFilePath(params) {
        // let path = `${environment.api.url}/@media/${this.uuid}`;
        let path = this.uuid;
        if (params) {
            // Object.assign(params, { upsize: false });
            path += `?${paramify(params)}`;
        }
        return path;
    }
    getIconString() {
        if (this.mime.startsWith('image/')) {
            return 'photo';
        }
        return 'insert_drive_file';
    }
    isBucket() {
        return false;
    }
    isImage() {
        return this.mime.startsWith('image/');
    }
    isVideo() {
        return this.mime.startsWith('video/');
    }
    get isPrivate() {
        return this.client_id && !this.unit_id;
    }
}
File.route = 'files';
File.fields = [
    'alias',
    'attributes',
    'bucket_id',
    'children',
    'client_id',
    'created_at',
    'id',
    'meta',
    'mime',
    'parent_id',
    'pivot',
    'public',
    'seen_at',
    'title',
    'unit_id',
    'updated_at',
    'uuid',
];
__decorate([
    Use(AttributeMixin, MetaMixin, CrudMixin)
], File.prototype, "this", void 0);
__decorate([
    Integer()
], File.prototype, "bucket_id", void 0);
__decorate([
    Integer()
], File.prototype, "unit_id", void 0);
__decorate([
    Integer()
], File.prototype, "client_id", void 0);
__decorate([
    Integer()
], File.prototype, "parent_id", void 0);
__decorate([
    Boolean()
], File.prototype, "public", void 0);
__decorate([
    Expand(Attribute, true)
], File.prototype, "attributes", void 0);
__decorate([
    Expand(File, true)
], File.prototype, "children", void 0);
__decorate([
    Expand(Meta, true)
], File.prototype, "meta", void 0);

class MediaMixin {
    constructor() {
        this.media = [];
        this.files = [];
    }
    hasMedia() {
        return this.media && this.media.length > 0;
    }
    hasFiles() {
        return this.files && this.files.length > 0;
    }
    setMedia(media) {
        this.media = [];
        for (const obj of media) {
            this.media.push(new File(obj));
        }
    }
    setFiles(files) {
        this.files = [];
        for (const obj of files) {
            this.files.push(new File(obj));
        }
    }
    getPrimaryMediaPath(params) {
        if (this.image) {
            return new File({ uuid: this.image }).getFilePath(params);
        }
        if (this.files && this.files.length) {
            return this.files[0].getFilePath(params);
        }
        if (this.media && this.media.length) {
            return this.media[0].getFilePath(params);
        }
        return null;
    }
}

class RoleMixin {
    hasRoleWithAlias(alias) {
        return this.roles?.find(r => r.alias === alias) ? true : false;
    }
    hasRoleWithKey(key) {
        return this.roles?.find(r => r.key === key) ? true : false;
    }
}

class Address extends AwareModel {
    constructor(data, model = Address) {
        super(data, model);
    }
}
Address.route = 'addresses';
Address.fields = ['alias', 'city', 'created_at', 'id', 'name', 'postal_code', 'resource_id', 'street', 'type', 'updated_at', 'uuid'];
__decorate([
    Integer()
], Address.prototype, "resource_id", void 0);

class Application extends AwareModel {
    constructor(data, model = Application) {
        super(data, model);
    }
}
Application.route = 'apps';
Application.fields = [
    'alias',
    'app_id',
    'config',
    'created_at',
    'deleted_at',
    'id',
    'media',
    'meta',
    'title',
    'unit_id',
    'updated_at',
    'uuid',
];
__decorate([
    Use(CrudMixin, MetaMixin)
], Application.prototype, "this", void 0);
__decorate([
    Integer()
], Application.prototype, "unit_id", void 0);
__decorate([
    Expand(Meta, true)
], Application.prototype, "meta", void 0);

class Contact extends AwareModel {
    constructor(data, model = Contact) {
        super(data, model);
    }
}
Contact.fields = [
    'alias',
    'created_at',
    'deleted_at',
    'id',
    'updated_at',
    'uuid',
    'resource_id',
    'name',
    'care_of',
    'line1',
    'line2',
    'city',
    'region',
    'postal_code',
    'country',
    'email',
    'phone',
    'meta',
    'type',
];
__decorate([
    Integer()
], Contact.prototype, "resource_id", void 0);

class Unit extends AwareModel {
    constructor(data, model = Unit) {
        super(data, model);
    }
}
Unit.route = 'units';
Unit.searchRoute = 'units/search';
Unit.fields = [
    'attached',
    'attributes',
    'alias',
    'children',
    'clients',
    'config',
    'content',
    'content_language',
    'contacts',
    'created_at',
    'deleted_at',
    'excerpt',
    'excerpt_language',
    'files',
    'hidden',
    'id',
    'image',
    'latitude',
    'location_id',
    'longitude',
    'media',
    'meta',
    'meta_content',
    'meta_content_language',
    'meta_title',
    'meta_title_language',
    'parent',
    'parent_id',
    'pivot-categories',
    'position',
    'public',
    'status',
    'title',
    'title_language',
    'type',
    'unit_id',
    'updated_at',
    'uuid',
];
__decorate([
    Use(AttributeMixin, CrudMixin, MetaMixin, MediaMixin)
], Unit.prototype, "this", void 0);
__decorate([
    Boolean()
], Unit.prototype, "attached", void 0);
__decorate([
    Boolean()
], Unit.prototype, "hidden", void 0);
__decorate([
    Boolean()
], Unit.prototype, "ignore", void 0);
__decorate([
    Boolean()
], Unit.prototype, "public", void 0);
__decorate([
    Expand(Attribute, true)
], Unit.prototype, "attributes", void 0);
__decorate([
    Expand(Unit, true)
], Unit.prototype, "children", void 0);
__decorate([
    Expand(File, true)
], Unit.prototype, "files", void 0);
__decorate([
    Expand(File, true)
], Unit.prototype, "media", void 0);
__decorate([
    Expand(Meta, true)
], Unit.prototype, "meta", void 0);
__decorate([
    Expand(Contact, true)
], Unit.prototype, "contacts", void 0);
__decorate([
    Expand(Unit)
], Unit.prototype, "parent", void 0);
__decorate([
    Float()
], Unit.prototype, "latitude", void 0);
__decorate([
    Float()
], Unit.prototype, "longitude", void 0);
__decorate([
    Integer()
], Unit.prototype, "client_id", void 0);
__decorate([
    Integer()
], Unit.prototype, "location_id", void 0);
__decorate([
    Integer()
], Unit.prototype, "position", void 0);
__decorate([
    Integer()
], Unit.prototype, "parent_id", void 0);
__decorate([
    Integer()
], Unit.prototype, "unit_id", void 0);

class Role extends AwareModel {
    constructor(data, model = Role) {
        super(data, model);
    }
}
Role.route = 'roles';
Role.fields = [
    'alias',
    'created_at',
    'children',
    'deleted_at',
    'id',
    'key',
    'permissions',
    'pivot',
    'title',
    'unit_id',
    'updated_at',
    'uuid',
    'parent_id',
];
__decorate([
    Use(CrudMixin)
], Role.prototype, "this", void 0);
__decorate([
    Integer()
], Role.prototype, "unit_id", void 0);
__decorate([
    Integer()
], Role.prototype, "parent_id", void 0);
__decorate([
    Expand(Role, true)
], Role.prototype, "children", void 0);

class Category extends AwareModel {
    constructor(data, model = Category) {
        super(data, model);
        this.color = this.getMeta('color');
        if (this.attr('color')) {
            this.color = this.attr('color');
        }
    }
}
Category.route = 'categories';
Category.searchRoute = 'categories/search';
Category.fields = [
    'attributes',
    'alias',
    'children',
    'content',
    'created_at',
    'excerpt',
    'files',
    'hidden',
    'id',
    'image',
    'location_id',
    'location',
    'media',
    'meta',
    'parent',
    'parent_id',
    'pivot-children',
    'pivot-parents',
    'position',
    'posts',
    'publish_at',
    'title',
    'toDelete',
    'type',
    'unit_id',
    'unit',
    'units',
    'updated_at',
    'uuid',
];
__decorate([
    Use(AttributeMixin, CrudMixin, MetaMixin, MediaMixin)
], Category.prototype, "this", void 0);
__decorate([
    Expand(Attribute, true)
], Category.prototype, "attributes", void 0);
__decorate([
    Boolean()
], Category.prototype, "hidden", void 0);
__decorate([
    Expand(Category)
], Category.prototype, "parent", void 0);
__decorate([
    Expand(Category, true)
], Category.prototype, "children", void 0);
__decorate([
    Expand(File, true)
], Category.prototype, "files", void 0);
__decorate([
    Expand(File, true)
], Category.prototype, "media", void 0);
__decorate([
    Expand(Meta, true)
], Category.prototype, "meta", void 0);
__decorate([
    Expand(Unit)
], Category.prototype, "unit", void 0);
__decorate([
    Expand(Role, true)
], Category.prototype, "roles", void 0);
__decorate([
    Integer()
], Category.prototype, "parent_id", void 0);
__decorate([
    Integer()
], Category.prototype, "unit_id", void 0);

class Bucket extends AwareModel {
    constructor(data, model = Bucket) {
        super(data, model);
        this.details = [];
        this._setDetails();
    }
    getIconString() {
        return 'folder';
    }
    isBucket() {
        return true;
    }
    isImage() {
        return false;
    }
    isVideo() {
        return false;
    }
    get isPrivate() {
        return this.client_id && !this.unit_id;
    }
    getFilePath(_) {
        return null;
    }
    _setDetails() {
        const details = [
            {
                label: 'Skapades',
                value: this.created_at,
            },
            {
                label: 'Ändrades',
                value: this.updated_at,
            },
        ];
        this.details = details.reverse();
    }
}
Bucket.route = 'buckets';
Bucket.fields = [
    'alias',
    'attributes',
    'children',
    'files',
    'client_id',
    'created_at',
    'id',
    'parent_id',
    'meta',
    'title',
    'unit_id',
    'updated_at',
    'uuid',
    'parent',
];
__decorate([
    Use(AttributeMixin, MetaMixin, CrudMixin)
], Bucket.prototype, "this", void 0);
__decorate([
    Integer()
], Bucket.prototype, "unit_id", void 0);
__decorate([
    Integer()
], Bucket.prototype, "client_id", void 0);
__decorate([
    Integer()
], Bucket.prototype, "parent_id", void 0);
__decorate([
    Expand(Meta, true)
], Bucket.prototype, "meta", void 0);
__decorate([
    Expand(Attribute, true)
], Bucket.prototype, "attributes", void 0);
__decorate([
    Expand(Bucket, true)
], Bucket.prototype, "children", void 0);
__decorate([
    Expand(File, true)
], Bucket.prototype, "files", void 0);
__decorate([
    Expand(Bucket)
], Bucket.prototype, "parent", void 0);
__decorate([
    Boolean()
], Bucket.prototype, "private", void 0);
__decorate([
    Boolean()
], Bucket.prototype, "public", void 0);

class Communication extends AwareModel {
    constructor(data, model = Communication) {
        super(data, model);
    }
}
Communication.route = 'communications';
Communication.fields = ['alias', 'client_id', 'created_at', 'id', 'sub_type', 'type', 'updated_at', 'uuid', 'value'];
__decorate([
    Integer()
], Communication.prototype, "client_id", void 0);

class Client extends AwareModel {
    constructor(data, model = Client) {
        super(data, model);
        this._activate();
    }
    get email() {
        const email = this.getCommunicationWithType('email');
        return email ? email.value : null;
    }
    get phone() {
        const phone = this.getCommunicationWithType('phone');
        return phone ? phone.value : null;
    }
    _activate() {
        this._setName();
        this._setRoleString();
    }
    _setName() {
        let name = '';
        let initials = '';
        if (this.given_name) {
            name = this.given_name;
            initials = this.given_name[0];
        }
        if (this.family_name) {
            name += ` ${this.family_name}`;
            initials += this.family_name[0];
        }
        this.name = name;
        this.initials = initials;
        this.title = this.name;
    }
    _setRoleString() {
        this.roleString = '';
        if (this.roles?.length) {
            this.role = this.roles[0];
            for (const role of this.roles) {
                if (this.roleString.length) {
                    this.roleString += ', ';
                }
                this.roleString += role.title;
            }
        }
    }
}
Client.route = 'clients';
Client.searchRoute = 'clients/search';
Client.fields = [
    'addresses',
    'alias',
    'attributes',
    'authenticated_at',
    'communications',
    'config',
    'created_at',
    'date_of_birth',
    'deleted_at',
    'family_name',
    'files',
    'gender',
    'given_name',
    'hidden',
    'id',
    'identity_no',
    'image',
    'last_activity_at',
    'latitude',
    'longitude',
    'media',
    'meta',
    'pivot',
    'roles',
    'seen_at',
    'status',
    'type',
    'units',
    'updated_at',
    'username',
    'uuid',
];
__decorate([
    Use(AddressMixin, CrudMixin, MetaMixin, MediaMixin, CommunicationMixin, AttributeMixin, RoleMixin)
], Client.prototype, "this", void 0);
__decorate([
    Boolean()
], Client.prototype, "hidden", void 0);
__decorate([
    Expand(Address, true)
], Client.prototype, "addresses", void 0);
__decorate([
    Expand(Attribute, true)
], Client.prototype, "attributes", void 0);
__decorate([
    Expand(Communication, true)
], Client.prototype, "communications", void 0);
__decorate([
    Expand(File, true)
], Client.prototype, "files", void 0);
__decorate([
    Expand(Meta, true)
], Client.prototype, "meta", void 0);
__decorate([
    Expand(File, true)
], Client.prototype, "media", void 0);
__decorate([
    Expand(Role)
], Client.prototype, "role", void 0);
__decorate([
    Expand(Role, true)
], Client.prototype, "roles", void 0);
__decorate([
    Expand(Unit, true)
], Client.prototype, "units", void 0);
__decorate([
    Float()
], Client.prototype, "latitude", void 0);
__decorate([
    Float()
], Client.prototype, "longitude", void 0);

class ContractSignature extends AwareModel {
    constructor(data, model = ContractSignature) {
        super(data, model);
    }
}
ContractSignature.fields = [
    'client',
    'client_id',
    'created_at',
    'deleted_at',
    'id',
    'proxy',
    'proxy_client_id',
    'resource_id',
    'signature',
    'social_no',
    'updated_at',
    'uuid',
];
__decorate([
    Expand(Client)
], ContractSignature.prototype, "client", void 0);
__decorate([
    Expand(Client)
], ContractSignature.prototype, "proxy", void 0);
__decorate([
    Integer()
], ContractSignature.prototype, "client_id", void 0);
__decorate([
    Integer()
], ContractSignature.prototype, "resource_id", void 0);

var ContractStatus;
(function (ContractStatus) {
    ContractStatus["Draft"] = "draft";
    ContractStatus["Published"] = "published";
})(ContractStatus || (ContractStatus = {}));
class Contract extends AwareModel {
    constructor(data, model = Contract) {
        super(data, model);
    }
    get isActive() {
        return this.isPublished && !this.isExpired;
    }
    get isPublished() {
        const publishAt = new Date(this.publish_at);
        const now = new Date();
        return publishAt < now;
    }
    get isExpired() {
        const now = new Date();
        const expireAt = this.expire_at ? new Date(this.expire_at) : null;
        return expireAt && expireAt < now;
    }
    get isEditable() {
        return this.signees_count <= 0;
    }
}
Contract.route = 'contracts';
Contract.searchRoute = 'contracts';
Contract.fields = [
    'alias',
    'children',
    'client_id',
    'content',
    'created_at',
    'expire_at',
    'id',
    'key',
    'parent',
    'parent_id',
    'publish_at',
    'revisions',
    'signature_deletable',
    'signature_uuid',
    'signatures',
    'signed_by_me',
    'signees_count',
    'title',
    'type',
    'unit_id',
    'unit',
    'updated_at',
    'uuid',
];
__decorate([
    Use(CrudMixin)
], Contract.prototype, "this", void 0);
__decorate([
    Expand(Contract)
], Contract.prototype, "parent", void 0);
__decorate([
    Expand(Contract, true)
], Contract.prototype, "children", void 0);
__decorate([
    Expand(Contract, true)
], Contract.prototype, "revisions", void 0);
__decorate([
    Expand(ContractSignature, true)
], Contract.prototype, "signatures", void 0);
__decorate([
    Expand(Unit)
], Contract.prototype, "unit", void 0);
__decorate([
    Integer()
], Contract.prototype, "client_id", void 0);
__decorate([
    Integer()
], Contract.prototype, "parent_id", void 0);
__decorate([
    Boolean()
], Contract.prototype, "signature_deletable", void 0);
__decorate([
    Boolean()
], Contract.prototype, "signed_by_me", void 0);
__decorate([
    Integer()
], Contract.prototype, "signees_count", void 0);
__decorate([
    Integer()
], Contract.prototype, "unit_id", void 0);

class Extension extends AwareModel {
    constructor(data, model = Extension) {
        super(data, model);
        if (data?.files?.length) {
            this.media = data.files;
        }
    }
}
Extension.route = 'extensions';
Extension.fields = [
    'alias',
    'attributes',
    'children',
    'config',
    'created_at',
    'currency',
    'description',
    'excerpt',
    'files',
    'id',
    'parent_id',
    'pivot',
    'price',
    'title',
    'type',
    'updated_at',
    'uuid',
];
__decorate([
    Use(AttributeMixin, CrudMixin, MediaMixin)
], Extension.prototype, "this", void 0);
__decorate([
    Expand(Attribute, true)
], Extension.prototype, "attributes", void 0);
__decorate([
    Expand(Extension, true)
], Extension.prototype, "children", void 0);
__decorate([
    Expand(File, true)
], Extension.prototype, "files", void 0);
__decorate([
    Expand(File, true)
], Extension.prototype, "media", void 0);
__decorate([
    Integer()
], Extension.prototype, "parent_id", void 0);
__decorate([
    Integer()
], Extension.prototype, "price", void 0);
__decorate([
    Json()
], Extension.prototype, "config", void 0);

class Feedback extends AwareModel {
    constructor(data, model = Feedback) {
        super(data, model);
    }
}
Feedback.fields = [
    'alias',
    'client',
    'client_id',
    'comment',
    'created_at',
    'deleted_at',
    'id',
    'language',
    'parent_id',
    'resource_id',
    'score',
    'title',
    'type',
    'updated_at',
    'uuid',
];
__decorate([
    Expand(Client)
], Feedback.prototype, "client", void 0);
__decorate([
    Integer()
], Feedback.prototype, "client_id", void 0);
__decorate([
    Integer()
], Feedback.prototype, "parent_id", void 0);
__decorate([
    Integer()
], Feedback.prototype, "resource_id", void 0);
__decorate([
    Integer()
], Feedback.prototype, "score", void 0);

class FieldAnswer extends AwareModel {
    constructor(data, model = FieldAnswer) {
        super(data, model);
    }
}
FieldAnswer.fields = ['value', 'value_extended', 'value_secondary', 'weight', 'meta'];
__decorate([
    Integer()
], FieldAnswer.prototype, "weight", void 0);

class Field extends AwareModel {
    constructor(data, model = Field) {
        super(data, model);
    }
}
Field.route = 'fields';
Field.fields = [
    'answers',
    'created_at',
    'id',
    'info',
    'options',
    'pivot',
    'position',
    'required',
    'title',
    'type',
    'unit_id',
    'updated_at',
    'uuid',
];
__decorate([
    Boolean()
], Field.prototype, "required", void 0);
__decorate([
    Expand(FieldAnswer, true)
], Field.prototype, "answers", void 0);
__decorate([
    Integer()
], Field.prototype, "category_id", void 0);
__decorate([
    Integer()
], Field.prototype, "position", void 0);

class FieldCategory extends Category {
    constructor(data, model = FieldCategory) {
        super(data, model);
        this.type = 'field_nav';
    }
}
FieldCategory.fields = ['fields', ...Category.fields];
__decorate([
    Expand(FieldCategory, true)
], FieldCategory.prototype, "children", void 0);
__decorate([
    Expand(Field, true)
], FieldCategory.prototype, "fields", void 0);

class Location extends AwareModel {
    constructor(data, model = Location) {
        super(data, model);
    }
}
Location.route = 'locations';
Location.searchRoute = 'locations/search';
Location.fields = [
    'children',
    'global',
    'id',
    'files',
    'latitude',
    'longitude',
    'meta',
    'parent_id',
    'parent',
    'polygon',
    'slug',
    'title',
    'type',
    'uuid',
    'viewport',
];
__decorate([
    Use(MetaMixin, MediaMixin)
], Location.prototype, "this", void 0);
__decorate([
    Boolean()
], Location.prototype, "hidden", void 0);
__decorate([
    Float()
], Location.prototype, "latitude", void 0);
__decorate([
    Float()
], Location.prototype, "longitude", void 0);
__decorate([
    Expand(Location, true)
], Location.prototype, "children", void 0);
__decorate([
    Expand(File, true)
], Location.prototype, "files", void 0);
__decorate([
    Expand(File, true)
], Location.prototype, "media", void 0);
__decorate([
    Expand(Location)
], Location.prototype, "parent", void 0);
__decorate([
    Expand(Meta, true)
], Location.prototype, "meta", void 0);
__decorate([
    Expand(Unit)
], Location.prototype, "unit", void 0);
__decorate([
    Integer()
], Location.prototype, "parent_id", void 0);

class Meeting extends AwareModel {
    constructor(data, model = Meeting) {
        super(data, model);
    }
}
Meeting.route = 'meetings';
Meeting.fields = ['alias', 'client_id', 'created_at', 'end_at', 'id', 'seats', 'start_at', 'type', 'unit_id', 'updated_at', 'uuid'];
__decorate([
    Integer()
], Meeting.prototype, "client_id", void 0);
__decorate([
    Integer()
], Meeting.prototype, "id", void 0);
__decorate([
    Integer()
], Meeting.prototype, "seats", void 0);
__decorate([
    Integer()
], Meeting.prototype, "unit_id", void 0);

class Reminder extends AwareModel {
    constructor(data, model = Reminder) {
        super(data, model);
        this._activate();
    }
    _activate() {
        if (Array.isArray(this.push)) {
            this.push = this.push[0];
        }
        if (Array.isArray(this.notification)) {
            this.notification = this.notification[0];
        }
    }
}
Reminder.route = 'reminders';
Reminder.fields = ['alias', 'created_at', 'date', 'id', 'meta', 'notification', 'offset', 'post_id', 'push', 'unit_id', 'updated_at', 'uuid'];
__decorate([
    Use(CrudMixin)
], Reminder.prototype, "this", void 0);
__decorate([
    Integer()
], Reminder.prototype, "offset", void 0);
__decorate([
    Integer()
], Reminder.prototype, "post_id", void 0);
__decorate([
    Integer()
], Reminder.prototype, "unit_id", void 0);
__decorate([
    Json()
], Reminder.prototype, "push", void 0);
__decorate([
    Json()
], Reminder.prototype, "notification", void 0);

class Post extends AwareModel {
    constructor(data, model = Post) {
        super(data, model);
    }
}
Post.route = 'posts';
Post.searchRoute = 'posts/search';
Post.fields = [
    'alias',
    'attached',
    'attributes',
    'break_at',
    'categories',
    'children',
    'client',
    'clients',
    'content',
    'content_language',
    'created_at',
    'deleted_at',
    'end_at',
    'excerpt',
    'excerpt_language',
    'expire_at',
    'files',
    'hidden',
    'id',
    'ignore',
    'image',
    'latitude',
    'location_id',
    'longitude',
    'media',
    'meetings',
    'meta',
    'parent',
    'parent_id',
    'pivot-children',
    'pivot-parents',
    'publish_at',
    'reminders',
    'schedule',
    'seen_at',
    'start_at',
    'status',
    'title',
    'title_language',
    'type',
    'unit',
    'units',
    'unit_id',
    'updated_at',
    'uuid',
    'visible',
    'stream',
];
Post.excludeFromHttp = ['categories'];
__decorate([
    Use(AttributeMixin, CrudMixin, MetaMixin, MediaMixin)
], Post.prototype, "this", void 0);
__decorate([
    Boolean()
], Post.prototype, "attached", void 0);
__decorate([
    Boolean()
], Post.prototype, "hidden", void 0);
__decorate([
    Boolean()
], Post.prototype, "ignore", void 0);
__decorate([
    Boolean()
], Post.prototype, "visible", void 0);
__decorate([
    Expand(Attribute, true)
], Post.prototype, "attributes", void 0);
__decorate([
    Expand(Category, true)
], Post.prototype, "categories", void 0);
__decorate([
    Expand(Post, true)
], Post.prototype, "children", void 0);
__decorate([
    Expand(Client)
], Post.prototype, "client", void 0);
__decorate([
    Expand(Client, true)
], Post.prototype, "clients", void 0);
__decorate([
    Expand(File, true)
], Post.prototype, "files", void 0);
__decorate([
    Expand(Meeting, true)
], Post.prototype, "meetings", void 0);
__decorate([
    Expand(Meta, true)
], Post.prototype, "meta", void 0);
__decorate([
    Expand(File, true)
], Post.prototype, "media", void 0);
__decorate([
    Expand(Post)
], Post.prototype, "parent", void 0);
__decorate([
    Expand(Post, true)
], Post.prototype, "pivot-children", void 0);
__decorate([
    Expand(Post, true)
], Post.prototype, "pivot-parents", void 0);
__decorate([
    Expand(Reminder, true)
], Post.prototype, "reminders", void 0);
__decorate([
    Expand(Unit)
], Post.prototype, "unit", void 0);
__decorate([
    Expand(Unit, true)
], Post.prototype, "units", void 0);
__decorate([
    Float()
], Post.prototype, "latitude", void 0);
__decorate([
    Float()
], Post.prototype, "longitude", void 0);
__decorate([
    Integer()
], Post.prototype, "client_id", void 0);
__decorate([
    Integer()
], Post.prototype, "parent_id", void 0);
__decorate([
    Integer()
], Post.prototype, "unit_id", void 0);
__decorate([
    Integer()
], Post.prototype, "stream", void 0);

class Message extends AwareModel {
    constructor(data, model = Message) {
        super(data, model);
    }
}
Message.route = 'messages';
Message.fields = [
    'alias',
    'attachments',
    'client',
    'client_id',
    'created_at',
    'files',
    'id',
    'message',
    'posts',
    'thread_id',
    'type',
    'updated_at',
    'uuid',
];
__decorate([
    Expand(Client)
], Message.prototype, "client", void 0);
__decorate([
    Expand(File, true)
], Message.prototype, "files", void 0);
__decorate([
    Expand(Post, true)
], Message.prototype, "posts", void 0);
__decorate([
    Integer()
], Message.prototype, "client_id", void 0);
__decorate([
    Integer()
], Message.prototype, "unit_id", void 0);

class Navigation extends AwareModel {
    constructor(data, model = Navigation) {
        super(data, model);
        if (this.posts?.length) {
            this.post = this.posts[0];
        }
    }
}
Navigation.route = 'categories';
Navigation.fields = [
    'alias',
    'attributes',
    'children',
    'content',
    'created_at',
    'excerpt',
    'files',
    'hidden',
    'id',
    'image',
    'location_id',
    'location',
    'media',
    'meta',
    'parent',
    'parent_id',
    'pivot-children',
    'pivot-parents',
    'position',
    'posts',
    'publish_at',
    'roles',
    'title',
    'toDelete',
    'type',
    'unit_id',
    'unit',
    'units',
    'updated_at',
    'url',
    'uuid',
];
__decorate([
    Use(AttributeMixin, CrudMixin, MetaMixin, MediaMixin)
], Navigation.prototype, "this", void 0);
__decorate([
    Boolean()
], Navigation.prototype, "hidden", void 0);
__decorate([
    Expand(Attribute, true)
], Navigation.prototype, "attributes", void 0);
__decorate([
    Expand(Navigation)
], Navigation.prototype, "parent", void 0);
__decorate([
    Expand(Navigation, true)
], Navigation.prototype, "children", void 0);
__decorate([
    Expand(File, true)
], Navigation.prototype, "files", void 0);
__decorate([
    Expand(Navigation, true)
], Navigation.prototype, "pivot-children", void 0);
__decorate([
    Expand(Navigation, true)
], Navigation.prototype, "pivot-parents", void 0);
__decorate([
    Expand(File, true)
], Navigation.prototype, "media", void 0);
__decorate([
    Expand(Meta, true)
], Navigation.prototype, "meta", void 0);
__decorate([
    Expand(Post)
], Navigation.prototype, "post", void 0);
__decorate([
    Expand(Post, true)
], Navigation.prototype, "posts", void 0);
__decorate([
    Expand(Role, true)
], Navigation.prototype, "roles", void 0);
__decorate([
    Expand(Unit)
], Navigation.prototype, "unit", void 0);
__decorate([
    Expand(Unit, true)
], Navigation.prototype, "units", void 0);
__decorate([
    Integer()
], Navigation.prototype, "parent_id", void 0);
__decorate([
    Integer()
], Navigation.prototype, "position", void 0);

class Notification extends AwareModel {
    constructor(data, model = Notification) {
        super(data, model);
    }
}
Notification.route = 'push';
Notification.fields = ['uuid', 'title', 'message', 'action', 'action_id', 'clients', 'topic'];

class Thread extends AwareModel {
    constructor(data, model = Thread) {
        super(data, model);
        this._activate();
    }
    activateWithMe(me) {
        this.setRecipients(me);
        this.setLastMessageClient(me);
        this.setUnreadMessages(me);
    }
    setLastMessageClient(me) {
        const uuid = this.last_message?.client;
        if (uuid && uuid !== me.uuid) {
            this.last_message_client = this.clients.find((client) => client.uuid === uuid);
        }
        else {
            this.last_message_client = this.clients.find((client) => client.uuid !== me.uuid);
        }
    }
    setRecipients(me) {
        this.recipients = this.clients.filter((c) => c.uuid !== me.uuid);
        this.title = this._setThreadTitle(this.recipients);
    }
    setUnreadMessages(me) {
        if (this.last_message && this.last_message.client !== me.uuid) {
            const meInClients = this.clients.find((c) => c.uuid === me.uuid);
            const lastTimeMeSeenIt = meInClients.seen_at;
            if (!lastTimeMeSeenIt) {
                return (this.hasUnreadMessages = true);
            }
            return (this.hasUnreadMessages = lastTimeMeSeenIt < this.last_message.created_at);
        }
    }
    _activate() {
        this.title = this._setThreadTitle(this.clients);
    }
    _setThreadTitle(recipients) {
        if (!recipients?.length) {
            return null;
        }
        const names = [];
        for (const client of recipients) {
            names.push(client.name);
        }
        return names.join(', ');
    }
}
Thread.route = 'threads';
Thread.searchRoute = 'threads/search';
Thread.fields = [
    'attributes',
    'clients',
    'created_at',
    'hasUnreadMessages',
    'id',
    'isGroupConversation',
    'last_message',
    'meetings',
    'meta',
    'title',
    'type',
    'unit_id',
    'updated_at',
    'uuid',
];
__decorate([
    Use(AttributeMixin, CrudMixin, MetaMixin)
], Thread.prototype, "this", void 0);
__decorate([
    Boolean()
], Thread.prototype, "hasUnreadMessages", void 0);
__decorate([
    Boolean()
], Thread.prototype, "isGroupConversation", void 0);
__decorate([
    Expand(Attribute, true)
], Thread.prototype, "attributes", void 0);
__decorate([
    Expand(Client, true)
], Thread.prototype, "clients", void 0);
__decorate([
    Expand(Meeting, true)
], Thread.prototype, "meetings", void 0);
__decorate([
    Expand(Meta, true)
], Thread.prototype, "meta", void 0);
__decorate([
    Expand(Message, true)
], Thread.prototype, "messages", void 0);
__decorate([
    Expand(Client, true)
], Thread.prototype, "recipients", void 0);
__decorate([
    Integer()
], Thread.prototype, "unit_id", void 0);
__decorate([
    Expand(Client)
], Thread.prototype, "last_message_client", void 0);

class Transaction extends AwareModel {
    constructor(data, model = Transaction) {
        super(data, model);
    }
}
Transaction.route = 'transactions';
Transaction.fields = [
    'alias',
    'amount',
    'attributes',
    'cancelled',
    'client',
    'client_id',
    'completed',
    'created_at',
    'currency',
    'deleted',
    'deleted_at',
    'delivered',
    'id',
    'meta',
    'order_no',
    'original_amount',
    'packaged',
    'paid_in',
    'paid_out',
    'parent',
    'parent_id',
    'payment_reference',
    'payment_status',
    'payment_type',
    'post',
    'post_id',
    'product_no',
    'quantity',
    'reference',
    'refunded',
    'refunded_amount',
    'sent',
    'type',
    'unit',
    'unit_id',
    'updated_at',
    'uuid',
    'vat',
    'vat_id',
    'verified',
    'name',
];
__decorate([
    Use(AttributeMixin, MetaMixin)
], Transaction.prototype, "this", void 0);
__decorate([
    Expand(Attribute, true)
], Transaction.prototype, "attributes", void 0);
__decorate([
    Expand(Client)
], Transaction.prototype, "client", void 0);
__decorate([
    Expand(Meta, true)
], Transaction.prototype, "meta", void 0);
__decorate([
    Expand(Transaction)
], Transaction.prototype, "parent", void 0);
__decorate([
    Expand(Post)
], Transaction.prototype, "post", void 0);
__decorate([
    Expand(Unit)
], Transaction.prototype, "unit", void 0);
__decorate([
    Float()
], Transaction.prototype, "amount", void 0);
__decorate([
    Float()
], Transaction.prototype, "original_amount", void 0);
__decorate([
    Float()
], Transaction.prototype, "refunded_amount", void 0);
__decorate([
    Float()
], Transaction.prototype, "vat", void 0);
__decorate([
    Integer()
], Transaction.prototype, "client_id", void 0);
__decorate([
    Integer()
], Transaction.prototype, "order_no", void 0);
__decorate([
    Integer()
], Transaction.prototype, "parent_id", void 0);
__decorate([
    Integer()
], Transaction.prototype, "post_id", void 0);
__decorate([
    Integer()
], Transaction.prototype, "quantity", void 0);
__decorate([
    Integer()
], Transaction.prototype, "unit_id", void 0);

class Version extends AwareModel {
    constructor(data, model = Version) {
        super(data, model);
    }
}
Version.route = 'versions';
Version.fields = [
    'alias',
    'app_id',
    'content',
    'commit',
    'created_at',
    'deleted_at',
    'env',
    'id',
    'prompt',
    'release_at',
    'required',
    'title',
    'updated_at',
    'uuid',
    'version',
];
__decorate([
    Use(CrudMixin)
], Version.prototype, "this", void 0);
__decorate([
    Integer()
], Version.prototype, "app_id", void 0);
__decorate([
    Boolean()
], Version.prototype, "prompt", void 0);
__decorate([
    Boolean()
], Version.prototype, "required", void 0);

/**
 * Function to serialize an aware model and its children,
 * for storing in local storage
 *
 * @param model AwareModel instance
 */
function serializeAwareModel(model, awareModelName) {
    const obj = {};
    const fields = [...model.model.fields, ...AwareModel.defaultFields];
    for (const field of fields) {
        let value = model[field];
        if (Array.isArray(value) && value[0] instanceof AwareModel) {
            value = value.map((v) => serializeAwareModel(v));
        }
        else if (value instanceof AwareModel) {
            value = serializeAwareModel(value);
        }
        obj[field] = value;
    }
    if (awareModelName) {
        obj.awareModelName = awareModelName;
    }
    return obj;
}

/**
 * Function to parse a meta object from a form instance for example, to inject into a model
 * to use in a http request or something
 *
 * @param meta any
 */
function parseMetaObjectForModel(meta) {
    const ret = [];
    for (const key in meta) {
        if (meta[key]) {
            ret.push(meta[key]);
        }
    }
    return ret;
}

/*
 * Public API Surface of @appbolaget/aware-model
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Address, AddressMixin, Application, Attribute, AttributeMixin, AwareModel, Boolean, Bucket, Category, Client, Communication, CommunicationMixin, Contact, Contract, ContractSignature, ContractStatus, CrudMixin, Expand, Extension, Feedback, Field, FieldAnswer, FieldCategory, File, Float, Integer, Json, Location, MediaMixin, Meeting, Message, Meta, MetaMixin, Navigation, Notification, Post, Reminder, Role, RoleMixin, Thread, Transaction, Unit, Version, parseMetaObjectForModel, serializeAwareModel };
